import React, { useEffect, useRef, useState } from 'react';

interface EditorToolsProps {
  variant: 'desktop' | 'mobile';
  onSave: () => void;
  onMergeP8?: (file: File) => void;
  onRotate: () => void;
  onFlip: () => void;
  onShiftRight?: () => void;
  onShiftDown?: () => void;
  onClear: () => void;
  saving: boolean;
  canSave: boolean;
  gridSize?: 8 | 16;
  onToggleGridSize?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onSelectFill?: () => void;
  fillActive?: boolean;
}

const EditorTools: React.FC<EditorToolsProps> = (props) => {
  const {
    variant, onSave, onRotate, onFlip, onShiftRight, onShiftDown, onClear,
    saving, canSave, onUndo, onRedo, canUndo, canRedo,
    gridSize, onToggleGridSize,     onSelectFill,
    fillActive
  } = props;

  // Transform-menyn (desktop/tablet)
  const [openTransform, setOpenTransform] = useState(false);
  const [dropDir, setDropDir] = useState<'down' | 'up'>('down');
  const transformRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Stil för aktiv FILL-knapp (desktop + mobil)
  const fillActiveStyle: React.CSSProperties | undefined = fillActive
    ? {
        background: '#0f2a1a',
        borderColor: '#41ff8b',
        boxShadow:
          '0 0 0 1px #41ff8b inset, 0 0 12px rgba(65,255,139,0.35)',
        color: '#eafff3',
      }
    : undefined;

  useEffect(() => {
    if (!openTransform) return;

    const decidePlacement = () => {
      const root = transformRef.current;
      if (!root) return;
      const btnRect = root.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight ?? 180;
      const spaceBelow = window.innerHeight - (btnRect.bottom + 8);
      const spaceAbove = btnRect.top - 8;
      setDropDir(spaceBelow < menuH && spaceAbove > menuH ? 'up' : 'down');
    };

    const onDocClick = (e: MouseEvent) => {
      if (!transformRef.current) return;
      if (!transformRef.current.contains(e.target as Node)) setOpenTransform(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenTransform(false); };

    decidePlacement();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('resize', decidePlacement);
    // lyssna på scroll i bubbling (t.ex. om verktygsraden ligger i scrollcontainer)
    window.addEventListener('scroll', decidePlacement, true);

    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('resize', decidePlacement);
      window.removeEventListener('scroll', decidePlacement, true);
    };
  }, [openTransform]);

  if (variant === 'desktop') {
    return (
      <div className="editor-tools desktop responsive-tools" style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        {onToggleGridSize && (
          <button className="tool-button" onClick={onToggleGridSize} title="Toggle 8×8 / 16×16" aria-label="Toggle sprite size 8×8 / 16×16">
            <svg className="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }} aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <line x1="16.65" y1="16.65" x2="21" y2="21" />
            </svg>
            <span className="label">{gridSize === 16 ? '16×16' : '8×8'}</span>
          </button>
        )}

        <button className="tool-button" onClick={onUndo} disabled={!canUndo} title="Undo (⌘/Ctrl+Z)">
          <span className="icon" aria-hidden="true"><span style={{ fontSize: 18, lineHeight: 1 }}>↶</span></span>
          <span className="label">UNDO</span>
        </button>
        <button className="tool-button" onClick={onRedo} disabled={!canRedo} title="Redo (⇧⌘/Ctrl+Y)">
          <span className="icon" aria-hidden="true"><span style={{ fontSize: 18, lineHeight: 1 }}>↷</span></span>
          <span className="label">REDO</span>
        </button>

        <button className="tool-button" onClick={onClear}>
          <span className="icon" aria-hidden="true">
            <svg viewBox="0 0 16 16"><path d="M2 4h12v2H2zM4 7h8l-1 7H5zM6 2h4l1 2H5z"/></svg>
          </span>
          <span className="label">CLEAR</span>
        </button>

        <button
          className="tool-button save-button"
          onClick={onSave}
          disabled={saving || canSave === false}
          aria-disabled={saving || canSave === false}
        >
          <span className="icon" aria-hidden="true">
            <svg viewBox="0 0 16 16"><path d="M2 2h10l2 2v10H2zM5 2h6v4H5zM4 9h8v5H4z"/></svg>
          </span>
          <span className="label">{saving ? 'Saving…' : canSave === false ? 'Saved' : 'Save'}</span>
        </button>

        <button
          className={`tool-button ${fillActive ? 'active' : ''}`}
          onClick={onSelectFill}
          aria-pressed={!!fillActive}
          title="Fill (bucket)"
          style={fillActiveStyle}  
    >
      <span className="icon" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="16" height="16">
          <path d="M3 2h6l4 4-5 5-5-5V2z" fill="currentColor"/>
          <path d="M11.5 9.8c-.9.5-1.5 1.1-1.5 1.8 0 .9 1.3 1.6 3 1.6s3-.7 3-1.6c0-.7-.6-1.3-1.5-1.8-.5-.3-1.5-.3-2 0z" fill="currentColor" opacity=".35"/>
        </svg>
      </span>
      <span className="label">FILL</span>
    </button>

        {/* TRANSFORM (drop-down/up) */}
        <div ref={transformRef} style={{ position:'relative' }}>
          <button
            className="tool-button"
            onClick={() => setOpenTransform(v => !v)}
            aria-expanded={openTransform}
            aria-haspopup="menu"
            title="Transform"
          >
            <span className="icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="16" height="16">
                <path d="M2 2h12v12H2zM4 4h8v8H4z" fill="currentColor" />
              </svg>
            </span>
            <span className="label">TRANSFORM</span>
          </button>

          {openTransform && (
            <div
              ref={menuRef}
              role="menu"
              className="transform-menu"
              style={{
                position:'absolute',
                ...(dropDir === 'down'
                  ? { top:'calc(100% + 6px)' }
                  : { bottom:'calc(100% + 6px)' }),
                left: '50%',                    // centrera meny horisontellt
                transform: 'translateX(-50%)',  // ─┘
                zIndex:1000,
                background:'#111',
                border:'1px solid #444',
                borderRadius:8,
                padding:6,
                boxShadow:'0 6px 18px rgba(0,0,0,0.4)',
                minWidth:160
              }}
            >
              <button
                className="tool-button"
                role="menuitem"
                onClick={() => { onRotate(); /* behåll öppet */ }}
                style={{ width:'100%', justifyContent:'center', gap:8 }}   // centrera innehåll
              >
                <span className="icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16"><path d="M8 2v3.2l3.6-2.4L8 0v3.2A5.8 5.8 0 1 0 13.8 9H11a3.8 3.8 0 1 1-3-6.2z"/></svg>
                </span>
                <span className="label">Rotate</span>
              </button>

              <button
                className="tool-button"
                role="menuitem"
                onClick={() => { onFlip(); /* behåll öppet */ }}
                style={{ width:'100%', justifyContent:'center', gap:8 }}
              >
                <span className="icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16"><path d="M2 2h4v12H2zM10 2h4v12h-4zM7 2h2v12H7z"/></svg>
                </span>
                <span className="label">Mirror</span>
              </button>

              <button
                className="tool-button"
                role="menuitem"
                onClick={() => { onShiftDown?.(); /* behåll öppet */ }}
                style={{ width:'100%', justifyContent:'center', gap:8 }}
              >
                <span className="icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="16" height="16"><path d="M3 6h10L8 13 3 6z" fill="currentColor" /></svg>
                </span>
                <span className="label">Down</span>
              </button>

              <button
                className="tool-button"
                role="menuitem"
                onClick={() => { onShiftRight?.(); /* behåll öppet */ }}
                style={{ width:'100%', justifyContent:'center', gap:8 }}
              >
                <span className="icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="16" height="16"><path d="M4 3l7 5-7 5V3z" fill="currentColor" /></svg>
                </span>
                <span className="label">Right</span>
              </button>
            </div>
          )}
        </div>

        
      </div>
    );
  }

  // Mobile: behåll separata knappar (oförändrat)
  return (
    <div className={`tool-buttons tool-buttons-${variant}`}>
      <button className="tool-button" onClick={onRotate} aria-label="Rotate">
        <span className="label">ROTATE</span>
      </button>
      <button className="tool-button" onClick={onFlip} aria-label="Mirror">
        <span className="label">MIRROR</span>
      </button>
      {/* <button className="tool-button" onClick={onShiftDown} aria-label="Shift Down">
        <span className="label">DOWN</span>
      </button> */}
      <button className="tool-button" onClick={onShiftRight} aria-label="Shift Right">
        <span className="label">RIGHT</span>
      </button>
      <button className="tool-button" onClick={onClear} aria-label="Clear">
        <span className="label">CLEAR</span>
      </button>
      <button
        className={`tool-button ${fillActive ? 'active' : ''}`}
        onClick={onSelectFill}
        aria-pressed={!!fillActive}
        title="Fill (bucket)" aria-label="Fill"
        style={fillActiveStyle} >

      <span className="label">FILL</span>
    </button>
      <button
        className="tool-button"
        onClick={onSave}
        disabled={saving || canSave === false}
        aria-disabled={saving || canSave === false}
        title={saving ? 'Saving...' : canSave === false ? 'No changes to save' : 'Save'}
      >
        <span className="label">{saving ? 'Saving…' : canSave === false ? 'Saved' : 'Save'}</span>
      </button>
    </div>
  );
};

export default EditorTools;